import { readPublicConfig } from "../config";
import type { BookingCatalog, BookingRequest, BookingResult } from "../domain/booking";
import type { AvailabilityQuery, AvailabilityResult } from "../domain/concierge";
import { bookingPersonRequest } from "./bookingHost";
import {
  bookableResources,
  catalogForResource,
  emptySelectionCatalog,
  selectBookingResource,
} from "./bookingPeople";
import { loadBookingPeopleContext } from "./bookingPeopleRepository";

const AVAILABILITY_CACHE_MS = 30_000;
const API_RETRY_DELAYS_MS = [180, 480];
const availabilityCache = new Map<string, { expiresAt: number; result: AvailabilityResult }>();
const pendingAvailabilityRequests = new Map<string, Promise<AvailabilityResult>>();

export type AppointmentPaymentChoice = "online" | "invoice";
export type AppointmentPaymentResult = {
  paymentChoice: AppointmentPaymentChoice | "not_required";
  paymentStatus: string;
  checkoutUrl?: string;
};

export type ManagedBooking = {
  reference: string;
  status: "confirmed" | "cancelled" | "completed" | "no_show";
  offerTitle: string;
  variantLabel: string;
  priceLabel: string;
  startsAt: string;
  endsAt: string;
  locationName: string;
  addressSummary: string;
  email: string;
  paymentChoice?: "online" | "invoice" | "not_required" | null;
  paymentStatus?: "not_required" | "unpaid" | "pending" | "paid" | "refunded" | "failed" | null;
  paymentMethod?: string | null;
  billingEntity?: "seminare" | "coaching" | "praxis" | null;
  stripeOnlineAllowed?: boolean;
  resourceSlug?: "rayk" | "katrin" | string | null;
  resourceName?: string | null;
};

export async function loadBookingCatalog(): Promise<BookingCatalog> {
  const config = readPublicConfig(import.meta.env);
  const response = await fetchWithTransientRetry(config.availabilityApiUrl, {
    method: "GET",
    headers: { Accept: "application/json" },
  });

  if (!response.ok) {
    throw new Error("Die aktuellen Angebote konnten nicht geladen werden.");
  }

  const catalog = (await response.json()) as BookingCatalog;
  if (catalog.source !== "live") {
    throw new Error("Die Buchungsstrecke ist noch nicht live verbunden.");
  }

  return scopeCatalogToRequestedPerson(filterCatalogForPublicPath(catalog));
}

async function scopeCatalogToRequestedPerson(catalog: BookingCatalog): Promise<BookingCatalog> {
  try {
    const context = await loadBookingPeopleContext();
    const resources = bookableResources(catalog, context);
    if (resources.length === 0) return catalog;

    const requestedPerson = bookingPersonRequest().slug;
    const selectedResource = selectBookingResource(resources, requestedPerson);

    if (!selectedResource) return emptySelectionCatalog(catalog);
    return catalogForResource(catalog, context, selectedResource.id);
  } catch (error) {
    console.warn("Booking person scope could not be applied.", error);
    return catalog;
  }
}

function settingLabelForPath(pathname: string, fallback: string): string {
  if (pathname === "/online") return "Online · Google Meet";
  if (pathname === "/berlin") return "Berlin · Präsenz";
  return fallback;
}

function filterCatalogForPublicPath(catalog: BookingCatalog): BookingCatalog {
  const pathname = window.location.pathname.replace(/\/+$/, "") || "/";
  const preferredLocationSlug =
    pathname === "/online"
      ? "google-meet"
      : pathname === "/berlin"
        ? "berlin-weissensee"
        : undefined;

  if (!preferredLocationSlug) return catalog;

  const locations = catalog.locations.filter((item) => item.slug === preferredLocationSlug);
  const locationIds = new Set(locations.map((item) => item.id));
  const offerLocations = catalog.offerLocations.filter((item) => locationIds.has(item.locationId));
  const offerIds = new Set(offerLocations.map((item) => item.offerId));

  return {
    ...catalog,
    offers: catalog.offers
      .filter((item) => offerIds.has(item.id))
      .map((item) => ({
        ...item,
        setting: settingLabelForPath(pathname, item.setting),
      })),
    variants: catalog.variants.filter((item) => offerIds.has(item.offerId)),
    locations,
    offerLocations,
    slots: catalog.slots.filter((item) => locationIds.has(item.bookingLocationId)),
  };
}

export async function loadAvailability(query: AvailabilityQuery): Promise<AvailabilityResult> {
  const key = availabilityKey(query);
  const now = Date.now();
  const cached = availabilityCache.get(key);
  if (cached && cached.expiresAt > now) return cached.result;
  if (cached) availabilityCache.delete(key);

  const pending = pendingAvailabilityRequests.get(key);
  if (pending) return pending;

  const request = fetchAvailability(query)
    .then((result) => {
      availabilityCache.set(key, { expiresAt: Date.now() + AVAILABILITY_CACHE_MS, result });
      return result;
    })
    .finally(() => pendingAvailabilityRequests.delete(key));

  pendingAvailabilityRequests.set(key, request);
  return request;
}

async function fetchAvailability(query: AvailabilityQuery): Promise<AvailabilityResult> {
  const config = readPublicConfig(import.meta.env);
  const response = await fetchWithTransientRetry(config.availabilityApiUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "availability", ...query }),
  });

  const payload = (await readJson(response)) as AvailabilityResult & { error?: string };
  if (!response.ok || payload.source !== "live") {
    throw new Error(toAvailabilityError(payload.error));
  }

  return payload;
}

function availabilityKey(query: AvailabilityQuery): string {
  return JSON.stringify({
    offerId: query.offerId,
    offerVariantId: query.offerVariantId,
    bookingLocationId: query.bookingLocationId,
    from: query.from ?? null,
    to: query.to ?? null,
    daypart: query.daypart ?? "any",
    limit: query.limit ?? 5,
  });
}

export async function createBooking(request: BookingRequest): Promise<BookingResult> {
  const config = readPublicConfig(import.meta.env);
  const response = await fetch(config.bookingApiUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      action: "book",
      slotId: request.slot.id,
      bookingLocationId: request.location.id,
      contact: request.contact,
    }),
  });

  const payload = (await readJson(response)) as {
    reference?: string;
    managementToken?: string;
    error?: string;
  };

  if (!response.ok || !payload.reference || !payload.managementToken) {
    throw new Error(toBookingError(payload.error));
  }

  return {
    reference: payload.reference,
    managementToken: payload.managementToken,
    source: "live",
  };
}

export async function configureAppointmentPayment(
  booking: BookingResult,
  paymentChoice: AppointmentPaymentChoice,
): Promise<AppointmentPaymentResult> {
  if (!booking.managementToken) throw new Error("Der Zahlungsweg konnte nicht vorbereitet werden.");
  const config = readPublicConfig(import.meta.env);
  const base = config.supabaseUrl ?? "https://cnejticdknokawqlslsj.supabase.co";
  const response = await fetch(`${base.replace(/\/$/, "")}/functions/v1/create-appointment-checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      reference: booking.reference,
      managementToken: booking.managementToken,
      paymentChoice,
    }),
  });
  const payload = (await readJson(response)) as AppointmentPaymentResult & { error?: string };
  if (!response.ok) {
    throw new Error(
      payload.error === "stripe_checkout_failed"
        ? "Der Termin ist gebucht, aber die sichere Zahlungsseite konnte gerade nicht geöffnet werden."
        : "Der Termin ist gebucht, aber der Zahlungsweg konnte gerade nicht gespeichert werden.",
    );
  }
  return payload;
}

export async function reconcileAppointmentPayment(sessionId: string): Promise<boolean> {
  const config = readPublicConfig(import.meta.env);
  const base = config.supabaseUrl ?? "https://cnejticdknokawqlslsj.supabase.co";
  const response = await fetch(`${base.replace(/\/$/, "")}/functions/v1/create-appointment-checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sessionId }),
  });
  const payload = (await readJson(response)) as { reconciled?: boolean; paid?: boolean };
  return response.ok && payload.reconciled === true && payload.paid === true;
}

export async function loadManagedBooking(
  reference: string,
  managementToken: string,
): Promise<ManagedBooking> {
  const config = readPublicConfig(import.meta.env);
  const response = await fetch(config.bookingApiUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "manage", reference, managementToken }),
  });

  const payload = (await readJson(response)) as ManagedBooking & { error?: string };
  if (!response.ok || !payload.reference) {
    throw new Error(
      payload.error === "booking_not_found"
        ? "Der Verwaltungslink ist ungültig oder nicht mehr verfügbar."
        : "Die Buchung konnte nicht geladen werden.",
    );
  }

  return payload;
}

export async function cancelManagedBooking(
  reference: string,
  managementToken: string,
): Promise<void> {
  const config = readPublicConfig(import.meta.env);
  const response = await fetch(config.bookingApiUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "cancel", reference, managementToken }),
  });

  const payload = (await readJson(response)) as { error?: string };
  if (!response.ok) {
    throw new Error(
      payload.error === "cancellation_too_late"
        ? "Der Termin kann online nicht mehr storniert werden. Bitte antworte auf deine Terminmail oder nutze die dort angegebene Booking-Adresse."
        : "Die Stornierung konnte nicht abgeschlossen werden.",
    );
  }
}

async function fetchWithTransientRetry(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= API_RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      const response = await fetch(input, init);
      if (!isTransientStatus(response.status) || attempt === API_RETRY_DELAYS_MS.length) return response;
    } catch (error) {
      lastError = error;
      if (attempt === API_RETRY_DELAYS_MS.length) throw error;
    }
    await delay(API_RETRY_DELAYS_MS[attempt] ?? 0);
  }
  throw lastError instanceof Error ? lastError : new Error("network_request_failed");
}

function isTransientStatus(status: number): boolean {
  return status === 401 || status === 408 || status === 429 || status >= 500;
}
function delay(ms: number) { return new Promise((resolve) => setTimeout(resolve, ms)); }
async function readJson(response: Response): Promise<unknown> { try { return await response.json(); } catch { return {}; } }
function toAvailabilityError(error?: string): string {
  switch (error) {
    case "invalid_date_range": return "Bitte einen gültigen Zeitraum auswählen.";
    case "date_range_too_large": return "Bitte einen Zeitraum von höchstens 93 Tagen auswählen.";
    case "invalid_selection": return "Die Auswahl ist nicht mehr gültig. Bitte Angebot, Dauer und Ort neu wählen.";
    case "google_authorization_failed": return "Die Kalenderverfügbarkeit kann gerade nicht sicher geprüft werden.";
    default: return "Passende Termine konnten gerade nicht geprüft werden.";
  }
}
function toBookingError(error?: string): string {
  switch (error) {
    case "slot_unavailable": return "Dieser Termin wurde gerade vergeben. Bitte wähle einen anderen Zeitpunkt.";
    case "calendar_conflict": return "Der Zeitraum ist im Kalender inzwischen belegt. Bitte wähle einen anderen Zeitpunkt.";
    case "calendar_sync_failed": return "Der Termin konnte nicht sicher in den Kalender übernommen werden. Es wurde nichts reserviert.";
    case "invalid_selection": return "Der gewählte Ort konnte nicht eindeutig bestätigt werden. Bitte Ort und Termin neu auswählen.";
    default: return "Die Buchung konnte nicht vollständig abgeschlossen werden.";
  }
}
